import {
  DEFAULT_SEARCH_FILTERS,
  HANDS_ON_LEARNING_TYPES_FACET_KEY,
  IS_PART_OF_COURSERA_PLUS,
  SKILLS_FACET_KEY,
  TOOL_SOFTWARE_SKILL_NAMES_FACET_KEY,
} from 'bundles/search-common/constants/filter';
import type { FacetConfig } from 'bundles/search-common/types/filter';

import _t from 'i18n!nls/search-common';

/**
 * Configuration object containing all available search facets and their properties.
 *
 * Each facet includes display names, property mapping, visibility settings,
 * and optional tooltips or support text for user guidance.
 */
export const FACETS_CONFIG: Record<string, FacetConfig> = {
  language: {
    getName: () => _t('Language'),
    getNameOnMobile: () => _t('Language'),
    property: 'language',
    showOutsideOfAllTab: false,
    showAllItems: false,
    getToolTip: () => _t('The language used throughout the course, in both instruction and assessments.'),
  },
  subtitleLanguage: {
    getName: () => _t('Subtitles'),
    getNameOnMobile: () => _t('Subtitles'),
    property: 'subtitleLanguage',
    showOutsideOfAllTab: false,
    showAllItems: false,
  },
  productDifficultyLevel: {
    getName: () => _t('Level'),
    getNameOnMobile: () => _t('Level'),
    property: 'productDifficultyLevel',
    showOutsideOfAllTab: false,
    showAllItems: true,
  },
  productDuration: {
    getName: () => _t('Duration'),
    getNameOnMobile: () => _t('Duration'),
    property: 'productDuration',
    showOutsideOfAllTab: false,
    showAllItems: true,
  },
  partners: {
    getName: () => _t('Educator'),
    getNameOnMobile: () => _t('Educator'),
    property: 'partners',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  skills: {
    getName: () => _t('Skills'),
    getNameOnMobile: () => _t('Skills'),
    property: 'skills',
    showOutsideOfAllTab: false,
    showAllItems: false,
  },
  productTypeDescription: {
    getName: () => _t('Learning Product'),
    getNameOnMobile: () => _t('Learning Product'),
    property: 'productTypeDescription',
    showOutsideOfAllTab: true,
    showAllItems: true,
  },
  [IS_PART_OF_COURSERA_PLUS]: {
    getName: (isCourseraLiteFilter?: boolean) => (isCourseraLiteFilter ? _t('Coursera Lite') : _t('Coursera Plus')),
    getNameOnMobile: (isCourseraLiteFilter?: boolean) =>
      isCourseraLiteFilter ? _t('Coursera Lite') : _t('Coursera Plus'),
    property: IS_PART_OF_COURSERA_PLUS,
    showOutsideOfAllTab: true,
    showAllItems: true,
  },
  topic: {
    getName: () => _t('Subject'),
    getNameOnMobile: () => _t('Subject'),
    property: 'topic',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  subtopic: {
    getName: () => _t('Subtopic'),
    getNameOnMobile: () => _t('Subtopic'),
    property: 'subtopic',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  hasLabAssignments: {
    getName: () => _t('Labs'),
    getNameOnMobile: () => _t('Labs'),
    property: 'hasLabAssignments',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  degreeLevel: {
    getName: () => _t('Program Level'),
    getNameOnMobile: () => _t('Program Level'),
    property: 'degreeLevel',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  partnerCountry: {
    getName: () => _t('Country'),
    getNameOnMobile: () => _t('Country'),
    property: 'partnerCountry',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  degreePathwayType: {
    getName: () => _t('Pathway Degrees'),
    getNameOnMobile: () => _t('Pathway Degrees'),
    property: 'degreePathwayType',
    showOutsideOfAllTab: true,
    showAllItems: false,
    getSupportText: () => _t('Build toward a degree by completing approved courses or programs'),
  },
  degreeMonthsTillNextAdmission: {
    getName: () => _t('Upcoming Application Deadline'),
    getNameOnMobile: () => _t('Upcoming Application Deadline'),
    property: 'degreeMonthsTillNextAdmission',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  degreeHasScholarship: {
    getName: () => _t('Financial Aid'),
    getNameOnMobile: () => _t('Financial Aid'),
    property: 'degreeHasScholarship',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  cost: {
    getName: () => _t('Cost'),
    getNameOnMobile: () => _t('Cost'),
    property: 'cost',
    showOutsideOfAllTab: true,
    showAllItems: true,
  },
  roles: {
    getName: () => _t('Role'),
    getNameOnMobile: () => _t('Role'),
    property: 'roles',
    showOutsideOfAllTab: true,
    showAllItems: false,
  },
  [HANDS_ON_LEARNING_TYPES_FACET_KEY]: {
    getName: () => _t('Hands-on Learning'),
    getNameOnMobile: () => _t('Hands-on Learning'),
    property: HANDS_ON_LEARNING_TYPES_FACET_KEY,
    showOutsideOfAllTab: false,
    showAllItems: true,
  },
  [TOOL_SOFTWARE_SKILL_NAMES_FACET_KEY]: {
    getName: () => _t('Tools'),
    getNameOnMobile: () => _t('Tools'),
    property: TOOL_SOFTWARE_SKILL_NAMES_FACET_KEY,
    showOutsideOfAllTab: false,
    showAllItems: false,
  },
};

/**
 * Extends the default facet list with additional filters based on configuration.
 *
 * @param defaultList - The base list of facet keys to extend
 * @param shouldIncludeCourseraPlusFacet - Whether to include the Coursera Plus filter
 * @returns Extended array of facet keys with additional filters prepended
 */
export function extendDefaultFacetList(defaultList: string[], shouldIncludeCourseraPlusFacet: boolean) {
  let facetList = [...defaultList];
  if (shouldIncludeCourseraPlusFacet && !defaultList.includes(IS_PART_OF_COURSERA_PLUS)) {
    // Coursera Plus filter should be the first one if exist
    facetList = [IS_PART_OF_COURSERA_PLUS, ...facetList];
  }

  return facetList;
}

/**
 * Configuration options for the getFacets function.
 */
type GetFacetsOptions = {
  /** Whether to include the Coursera Plus filter in the facet list */
  shouldIncludeCourseraPlusFacet?: boolean;
  /** Whether to exclude the skills facet from the facet list */
  shouldExcludeSkillsFacet?: boolean;
  /** Custom order for facets. If not provided, uses default order with conditional additions */
  facetOrder?: string[];
};

/**
 * Generates an array of facet configurations based on the provided options.
 *
 * This function creates a list of search facets (filters) with their configuration
 * details, allowing for customization of which facets to include and their order.
 *
 * @param options - Configuration options for determining which facets to include
 * @param options.shouldIncludeCourseraPlusFacet - Whether to include the Coursera Plus facet
 * @param options.shouldExcludeSkillsFacet - Whether to exclude the skills facet
 * @param options.facetOrder - Custom order for facets. If not provided, uses default order
 * @returns Array of facet configurations that can be used to render search filters
 *
 * @example
 * // Get default facets
 * const defaultFacets = getFacets();
 *
 * @example
 * // Get facets with custom order and Coursera Plus filter
 * const customFacets = getFacets({
 *   facetOrder: ['topic', 'language', 'partners'],
 *   shouldIncludeCourseraPlusFacet: true,
 * });
 */
export const getFacets = (options?: GetFacetsOptions): FacetConfig[] => {
  const { shouldIncludeCourseraPlusFacet, facetOrder, shouldExcludeSkillsFacet } = options || {};

  let facetKeys = facetOrder ?? extendDefaultFacetList(DEFAULT_SEARCH_FILTERS, !!shouldIncludeCourseraPlusFacet);

  if (shouldExcludeSkillsFacet) {
    facetKeys = facetKeys.filter((key) => key !== SKILLS_FACET_KEY);
  }

  return facetKeys.map((facetKey) => FACETS_CONFIG[facetKey]).filter(Boolean);
};

export default getFacets;
